import { useEffect, useState } from 'react';
import { login } from '@/utils/auth';
import AbstractAvatar from '@/components/Avatars/AbstractAvatar';
import AdminAvatarMenu from '@/components/Avatars/AdminAvatarMenu';
import LevelBadge from '@/components/Levels/LevelBadge';
import { getLevelColor } from '@/utils/colors';
import SkinsRow from '@/components/Jackpot/Game/SkinsRow';
import CoinflipInlineCoin from './CoinflipInlineCoin';
import CoinflipMiniTimer from './CoinflipMiniTimer';
import { ECoinflipSide, ICoinflipGame } from '@/utils/types/ICoinflipGame';
import { IUser } from '@/utils/types/User';
import { IGameItem } from '@/utils/types/Items';
import { itemsValue } from '@/utils/itemInfo';
import { modalStore } from '@/stores/modal.store';
import useUserStore from '@/stores/user.store';
import { notify } from '@/utils/notify';
import { requireTradeLink } from '@/utils/requireTradeLink';
import AlertIcon from '@/components/Notifications/AlertIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import { useSyncedCountdown } from '@/utils/hooks/use-synced-countdown';
import Login from '@/components/Login/Login';

interface Props {
    game: ICoinflipGame;
    history?: boolean;
}

// Square avatar (rounded-square, mirroring the jackpot look) with the user's
// LEVEL-colour ring + a solid level badge in the corner.
const Avatar = ({
    user,
    winner,
    gameId,
}: {
    user: IUser;
    winner?: boolean;
    gameId: number;
}) => {
    const lvl = Math.max(0, Math.floor((user.experience ?? 0) / 100));
    const color = getLevelColor(lvl);
    return (
        <div
            className="relative w-[60px] h-[60px]"
            style={{ ['--ring-color' as string]: color } as React.CSSProperties}
        >
            <AdminAvatarMenu userId={user.id} userName={user.name} gameType="coinflip" gameId={gameId}>
                <AbstractAvatar
                    seed={user.name || String(user.id)}
                    src={user.avatar}
                    size={60}
                    square
                    className="!w-full !h-full"
                />
            </AdminAvatarMenu>
            <span
                className="absolute -inset-1 rounded-[30%] pointer-events-none"
                style={{ border: `2.5px solid ${color}`, boxShadow: `0 0 18px -3px ${color}` }}
            />
            <div className="absolute -right-1.5 -bottom-1.5">
                <LevelBadge level={lvl} color={getLevelColor(lvl)} size="sm" variant="solid" />
            </div>
            {winner && (
                <span
                    className="jp-win-flag absolute -top-2.5 left-1/2 -translate-x-1/2 z-10 !text-[9px] !px-2 !py-px !gap-0.5 whitespace-nowrap"
                    style={{ letterSpacing: '0.08em' }}
                >
                    👑 WIN
                </span>
            )}
        </div>
    );
};

const EmptySlot = ({ gameId, side }: { gameId?: number; side?: 1 | 2 }) => {
    const slot = (
        <div
            className="group/slot relative w-[60px] h-[60px] rounded-[28%] grid place-items-center text-p2 cursor-pointer transition-transform hover:-translate-y-0.5"
            style={{
                background:
                    'linear-gradient(160deg, rgba(70,224,138,0.16), rgba(70,224,138,0.02))',
            }}
        >
            <svg
                viewBox="0 0 24 24"
                className="w-6 h-6 stroke-current fill-none [stroke-width:2.4] [stroke-linecap:round] [stroke-linejoin:round] transition-transform group-hover/slot:scale-110"
                style={{ filter: 'drop-shadow(0 0 6px rgba(70,224,138,0.6))' }}
            >
                <path d="M12 5v14M5 12h14" />
            </svg>
            <span
                className="absolute -inset-1 rounded-[30%] pointer-events-none transition-all group-hover/slot:-inset-[5px]"
                style={{
                    border: '2.5px dashed rgba(70,224,138,0.55)',
                    boxShadow:
                        '0 0 14px -2px rgba(70,224,138,0.5), inset 0 0 16px -6px rgba(70,224,138,0.6)',
                }}
            />
        </div>
    );
    if (gameId === undefined) return slot;
    return (
        <AdminAvatarMenu
            userId={0}
            userName="empty slot"
            gameType="coinflip"
            gameId={gameId}
            position={side === 1 ? 'right' : 'left'}
        >
            {slot}
        </AdminAvatarMenu>
    );
};

const Versus = ({
    game,
    history,
    flipping,
    winnerSide,
}: {
    game: ICoinflipGame;
    history?: boolean;
    flipping?: boolean;
    winnerSide?: 1 | 2;
}) => {
    const pendingUser = game.pendingJoin?.user as IUser | undefined;
    const displaySecondUser = game.secondUser ?? pendingUser;
    const leftUser = game.firstSide === ECoinflipSide.FIRST ? game.firstUser : displaySecondUser;
    const rightUser = game.firstSide === ECoinflipSide.FIRST ? displaySecondUser : game.firstUser;

    if (flipping && leftUser && rightUser) {
        return (
            <CoinflipInlineCoin
                p1={leftUser}
                p2={rightUser}
                winnerSide={winnerSide ?? 1}
                flipStartAt={game.flipStartAt}
                revealAt={game.revealAt}
            />
        );
    }

    return (
        <div className="flex items-center gap-3.5 relative">
            {leftUser ? (
                <Avatar
                    user={leftUser}
                    winner={history && game.winnerSide === ECoinflipSide.FIRST}
                    gameId={game.id}
                />
            ) : (
                <EmptySlot gameId={game.id} side={1} />
            )}
            <span
                className="font-chat-nick text-[14px] font-extrabold tracking-[0.12em] relative"
                style={{
                    background: 'linear-gradient(135deg, #ffd66b, #ff9d3d)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                }}
            >
                VS
            </span>
            {rightUser ? (
                <Avatar
                    user={rightUser}
                    winner={history && game.winnerSide === ECoinflipSide.SECOND}
                    gameId={game.id}
                />
            ) : (
                <EmptySlot gameId={game.id} side={2} />
            )}
        </div>
    );
};

const CoinflipRow = ({ game, history }: Props) => {
    const { user, isAuthed } = useUserStore((state) => state);
    const { setTitle, setComponent, setData, toggleModal } = modalStore((state) => state);

    // Hold local "fake" winnerSide for inline coin animation while waiting for backend
    const [pendingFlip, setPendingFlip] = useState(false);
    const [leaving, setLeaving] = useState(false);

    const items = [...game.firstItems, ...game.secondItems];
    const value = itemsValue(items);
    const range: [number, number] = [value * 0.9, value * 1.1];

    const winnerSide = game.winnerSide as 1 | 2 | undefined;
    const flipping = !!winnerSide && !history;

    useEffect(() => {
        if (winnerSide && !history) {
            // Spin the avatars, keep the result on the main list for ~15s, then
            // play the exit just before the store drops it from ACTIVE.
            setPendingFlip(true);
            const t = setTimeout(() => setLeaving(true), 14300);
            return () => clearTimeout(t);
        }
    }, [winnerSide, history]);

    function view() {
        if (history) {
            setTitle(`COINFLIP #${game.id}`);
            setComponent('View coinflip');
            setData({ game });
            toggleModal(true);
        } else {
            setTitle(`COINFLIP #${game.id}`);
            setComponent('View coinflip');
            setData({ id: game.id });
            toggleModal(true);
        }
    }

    function join() {
        if (!isAuthed) {
            login();
            return;
        }
        if (!user.tradeToken) {
            requireTradeLink();
            return;
        }
        setTitle('MAKE DEPOSIT');
        setComponent('Make deposit');
        setData({
            siteFunc: () => {
                setTitle(`JOIN COINFLIP #${game.id}`);
                setComponent('Join coinflip');
                setData({ id: game.id });
            },
            steamFunc: () => {
                setTitle(`JOIN COINFLIP #${game.id}`);
                setComponent('Join steam coinflip');
                setData({ id: game.id });
            },
        });
        toggleModal(true);
    }

    const isOwner = game.firstUser.id === user.id;
    const hasOpponent = !!game.secondUser;
    const isReserved = !!game.pendingJoin;
    const showJoin = !history && !flipping && !isOwner && !hasOpponent && !isReserved;
    const seconds = useSyncedCountdown(game.endsAt ?? null, 15, 'floor');
    const pendingSeconds = useSyncedCountdown(game.pendingJoin?.expiresAt ?? null, 120, 'floor');
    const showTimer = !history && hasOpponent && !flipping && !!game.endsAt;

    const amountChip = (
        <div
            className="inline-flex items-center gap-2 px-4 max-md:px-3 py-2 max-md:py-1.5 rounded-fl whitespace-nowrap"
            style={{
                border: '1px solid color-mix(in srgb, #3ad6ff 40%, transparent)',
                background:
                    'linear-gradient(135deg, color-mix(in srgb, #8b6dff 18%, transparent), color-mix(in srgb, #3ad6ff 10%, transparent))',
                boxShadow:
                    '0 0 20px -6px #3ad6ff, inset 0 0 16px -4px color-mix(in srgb, #8b6dff 38%, transparent)',
            }}
        >
            <span
                className="grid place-items-center shrink-0 rounded-[5px]"
                style={{
                    width: 18,
                    height: 18,
                    background: 'linear-gradient(135deg, #8b6dff, #3ad6ff)',
                    boxShadow: '0 0 10px -1px rgba(58,214,255,0.75)',
                }}
            >
                <span
                    className="font-black leading-none text-white"
                    style={{ fontSize: 10, textShadow: '0 1px 1px rgba(0,0,0,0.35)' }}
                >
                    $
                </span>
            </span>
            <span
                className="font-chat-nick text-[17px] max-md:text-[15px] font-extrabold tabular-nums"
                style={{
                    background: 'linear-gradient(135deg, #b7a6ff, #5fe0ff)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                }}
            >
                {value.toFixed(2)}
            </span>
        </div>
    );

    return (
        <div
            className={`cf-row-enter ${leaving ? 'cf-row-leaving' : ''} ${history ? 'opacity-90' : ''} relative bg-layer_1 border border-stroke rounded-fl-md transition-all hover:bg-layer_2 hover:border-stroke_strong cursor-pointer
                grid items-center gap-6 py-5 px-6
                grid-cols-[240px_minmax(0,1fr)_auto_auto_auto_auto]
                max-2xl:grid-cols-[220px_minmax(0,1fr)_auto_auto] max-2xl:gap-4
                max-md:!flex max-md:!flex-col max-md:items-stretch max-md:gap-3 max-md:p-4 ${
                    flipping ? 'border-accent_5/30' : ''
                }`}
            style={
                flipping
                    ? { background: 'linear-gradient(90deg, rgba(255,194,77,0.06), transparent 50%)' }
                    : undefined
            }
            onClick={view}
        >
            <div className="max-md:flex max-md:justify-center">
                <Versus
                    game={game}
                    history={history}
                    flipping={pendingFlip}
                    winnerSide={winnerSide}
                />
            </div>

            <SkinsRow skins={items} tile={60} gap={8} radius={9} mobileHidden={false} />

            {/* Amount — desktop column. On mobile it sits inside the actions row below. */}
            <div className="max-md:hidden">{amountChip}</div>

            <div className="font-chat-nick flex items-center gap-1.5 text-sm whitespace-nowrap max-2xl:hidden">
                {game.pendingJoin ? (
                    <span className="max-w-[180px] truncate text-text_3">
                        <b className="text-text_main">{game.pendingJoin.user.name}</b>{' '}
                        {game.pendingJoin.source === 'STEAM' ? 'confirming trade' : 'joining'}
                    </span>
                ) : (
                    <>
                        <span className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-text_4">
                            Needs
                        </span>
                        <b
                            className="font-extrabold tabular-nums"
                            style={{
                                color: '#5fe0ff',
                                textShadow: '0 0 10px rgba(58,214,255,0.5)',
                            }}
                        >
                            ${range[0].toFixed(2)}
                        </b>
                        <span className="text-text_4">–</span>
                        <b
                            className="font-extrabold tabular-nums"
                            style={{
                                color: '#5fe0ff',
                                textShadow: '0 0 10px rgba(58,214,255,0.5)',
                            }}
                        >
                            ${range[1].toFixed(2)}
                        </b>
                    </>
                )}
            </div>

            <div className="inline-flex items-center gap-1.5 text-sm text-text_2 font-semibold tabular-nums bg-layer_2 border border-stroke rounded-lg px-3 py-1.5 max-2xl:hidden">
                <svg
                    viewBox="0 0 24 24"
                    className="w-3.5 h-3.5 opacity-60 stroke-current fill-none [stroke-width:2]"
                >
                    <rect x="3" y="7" width="18" height="13" rx="2" />
                    <path d="M8 7V5a4 4 0 0 1 8 0v2" />
                </svg>
                {items.length}
            </div>

            <div
                className="flex items-center gap-2.5 max-md:justify-between max-md:gap-2"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Amount inline on mobile */}
                <div className="md:hidden">{amountChip}</div>
                <div className="flex items-center gap-2.5 max-md:gap-2">
                    <button
                        onClick={view}
                        className="inline-flex items-center gap-2 px-4 max-md:px-3 py-2.5 max-md:py-2 rounded-fl text-[13px] max-md:text-[11.5px] font-bold tracking-[0.06em] border border-stroke bg-layer_2 text-text_2 hover:bg-layer_3 hover:text-text_main hover:border-stroke_strong hover:-translate-y-0.5 transition-all"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            className="w-4 h-4 max-md:w-3.5 max-md:h-3.5 stroke-current fill-none [stroke-width:2]"
                        >
                            <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
                            <circle cx="12" cy="12" r="3" />
                        </svg>
                        VIEW
                    </button>
                    {showJoin &&
                        (isAuthed ? (
                            <button
                                onClick={join}
                                className="group relative overflow-hidden inline-flex items-center gap-2 px-5 max-md:px-3.5 py-2.5 max-md:py-2 rounded-full text-[13px] max-md:text-[11.5px] font-extrabold tracking-[0.1em] text-[#04150c] hover:-translate-y-0.5 transition-transform"
                                style={{
                                    background:
                                        'linear-gradient(135deg, #7df3b4 0%, #46e08a 45%, #1f9f7a 100%)',
                                    boxShadow:
                                        'inset 0 1px 0 rgba(255,255,255,0.5), inset 0 0 0 1px rgba(255,255,255,0.16), 0 0 26px -4px rgba(70,224,138,0.8), 0 8px 22px -6px rgba(31,159,122,0.6)',
                                }}
                            >
                                {/* glossy shine that sweeps across on hover */}
                                <span
                                    aria-hidden
                                    className="pointer-events-none absolute inset-0 -translate-x-[120%] group-hover:translate-x-[120%] transition-transform duration-[700ms] ease-out"
                                    style={{
                                        background:
                                            'linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.6), transparent 70%)',
                                    }}
                                />
                                {/* crossed swords — joining the 1v1 duel */}
                                <svg
                                    viewBox="0 0 24 24"
                                    className="relative w-4 h-4 max-md:w-3.5 max-md:h-3.5 stroke-current fill-none [stroke-width:2.2] [stroke-linecap:round] [stroke-linejoin:round] transition-transform group-hover:rotate-[-8deg]"
                                >
                                    <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" />
                                    <line x1="13" x2="19" y1="19" y2="13" />
                                    <line x1="16" x2="20" y1="16" y2="20" />
                                    <line x1="19" x2="21" y1="21" y2="19" />
                                    <polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5" />
                                    <line x1="5" x2="9" y1="14" y2="18" />
                                    <line x1="7" x2="4" y1="17" y2="20" />
                                    <line x1="3" x2="5" y1="19" y2="21" />
                                </svg>
                                <span className="font-chat-nick relative">JOIN</span>
                            </button>
                        ) : (
                            <Login className="px-4 max-md:px-3 py-2.5 max-md:py-2 rounded-fl text-[13px] max-md:text-[11.5px]" />
                        ))}
                    {isReserved && <CoinflipMiniTimer seconds={pendingSeconds} max={120} />}
                    {showTimer && <CoinflipMiniTimer seconds={seconds} max={15} />}
                </div>
            </div>
        </div>
    );
};

export default CoinflipRow;
